// ABOUTME: Cavalry WASM player for the boot-loader logomark (Figma "Ophion Loader" frame).
// ABOUTME: Plays OphionLoaderAnimation.cv once and holds the last frame; falls back to the static mark on error.

import { useEffect, useRef, useState } from 'react';
import { getCavalryModule, releaseSurface, drainPendingAssets } from '../CavalryViz';

const SCENE_PATH = '/OphionLoaderAnimation.cv';
// Distinct from CavalryViz's 'scene.cv'/'#canvas' targets so the two players never collide if a
// render frame from one overlaps the other's mount/unmount.
const FS_FILENAME = 'loader-scene.cv';
const TARGET_ID = 'cavalry-loader-canvas';

// Comp attributes read from the .cv JSON (compNode#8: fps 60, inTime 118, playbackEnd 198) — the
// authored rig is 80 frames at 60fps. Node ids renumber on every Cavalry re-export (see
// CavalryViz.js's NODES comment), so this is a duration in ms, not a node reference, and survives
// re-exports as long as the timing itself is unchanged.
export const LOADER_ANIMATION_DURATION_MS = Math.round(((198 - 118) / 60) * 1000);

let playerPromise = null;
let sharedDims = null;

function getPlayer() {
  if (!playerPromise) {
    playerPromise = (async () => {
      const Module = await getCavalryModule();

      const res = await fetch(SCENE_PATH);
      if (!res.ok) throw new Error(`Scene fetch failed: ${res.status}`);
      const bytes = new Uint8Array(await res.arrayBuffer());

      try { Module.FS.unlink(FS_FILENAME); } catch (_) {}
      Module.FS.writeFile(FS_FILENAME, bytes);

      const player = Module.Cavalry.MakeWithPath(FS_FILENAME);

      // Drain-and-reset — the queue lives on the Module we share with CavalryViz. See its comment.
      const pendingAssets = drainPendingAssets(Module);
      if (pendingAssets.length) {
        await Promise.all(pendingAssets.map(async (asset) => {
          const r = await fetch(`/${asset.filename}`);
          if (!r.ok) { console.warn('[CavalryLoaderMark] asset not found:', asset.filename, r.status); return; }
          const buf = new Uint8Array(await r.arrayBuffer());
          Module.FS.writeFile(asset.filename, buf);
          if (asset.type === 'image') player.replaceImageAsset(asset.filename, asset.assetId);
          else if (asset.type === 'font') { Module.loadFont(asset.filename); player.replaceFontAsset(asset.filename, asset.assetId); }
        }));
      }

      const { width, height } = player.getSceneResolution();
      sharedDims = { w: width, h: height };
      return { Module, player };
    })()
      .catch((err) => { playerPromise = null; throw err; });
  }
  return playerPromise;
}

function bindSurface(Module, player, canvas, dims) {
  canvas.id = TARGET_ID;
  Module.specialHTMLTargets = Module.specialHTMLTargets ?? {};
  Module.specialHTMLTargets[`#${TARGET_ID}`] = canvas;

  canvas.width = 0;
  canvas.height = 0;
  canvas.width = dims.w;
  canvas.height = dims.h;

  const surface = Module.makeWebGLSurfaceFromElement(canvas, dims.w, dims.h);
  player.render(surface);
  if (!player.isPlaying()) player.play();
  return surface;
}

/**
 * @param {{ maxWidth?: number|string, onComplete?: () => void, onError?: (err: Error) => void }} props
 */
export default function CavalryLoaderMark({ maxWidth = 128, onComplete, onError }) {
  const canvasRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const [error, setError] = useState(null);
  const [dims, setDims] = useState(sharedDims);

  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    let rafId = null;
    let aborted = false;
    let done = false;
    // This loader is short-lived and unmounts before the guest even enters, but its WebGL context
    // used to outlive it on the shared Emscripten Module — so the voice visualizer's scene was
    // later built under THIS dead context and rendered nothing. See releaseSurface in CavalryViz.
    let bound = null; // { Module, player, surface }

    function startLoop(player, surface) {
      let baseTs = null;
      const tick = (ts) => {
        if (aborted) return;
        if (baseTs === null) baseTs = ts;
        try {
          player.tick(surface, ts);
        } catch (e) {
          console.error('[CavalryLoaderMark] tick error:', e);
        }
        // Plays once then holds the last rendered frame — the loader unmounts on its own fixed
        // beat (App.js's LOADER_DISPLAY_MS) shortly after, so there is nothing to loop back to.
        if (ts - baseTs >= LOADER_ANIMATION_DURATION_MS) {
          if (!done) { done = true; onCompleteRef.current?.(); }
          return;
        }
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }

    async function init() {
      try {
        const { Module, player } = await getPlayer();
        if (aborted) return;
        setDims(sharedDims);
        const surface = bindSurface(Module, player, canvas, sharedDims);
        bound = { Module, player, surface };
        startLoop(player, surface);
      } catch (err) {
        console.error('[CavalryLoaderMark] init error:', err);
        setError(err.message);
        onErrorRef.current?.(err);
        if (!done) { done = true; onCompleteRef.current?.(); }
      }
    }

    init();

    return () => {
      aborted = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (bound) {
        releaseSurface({ ...bound, canvas, targetKey: `#${TARGET_ID}` });
        bound = null;
      }
      // The parsed scene is cached in `playerPromise`, but its GPU-side resources belonged to the
      // context just released — reusing it after a remount would draw with dead handles. Dropping
      // the cache costs one cheap re-parse (the WASM module itself stays warm) and is the only
      // way a second boot renders correctly.
      playerPromise = null;
    };
  }, []);

  const displayW = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
  const aspectRatio = dims ? dims.w / dims.h : 1;

  if (error) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{ width: displayW, aspectRatio, display: 'block' }}
    />
  );
}
